import React, { useEffect, useState } from "react";
import { Box, Collapse, Menu, MenuItem, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CaretDown,
  CaretRight,
  Folder as FolderIcon,
  PencilSimpleLine,
  Trash,
} from "@phosphor-icons/react";
import { Folder, Flows } from "../../../../util/Types/Folder";
import { FolderService } from "../../../../service/FolderService";
import { isVirtualFolderId } from "../utils";
import { useAgentsSidebarContext } from "./context";
import { AgentItem } from "./AgentItem";
import { CustomColorPalette } from "../../../../theme";

const FolderHeader = styled(Box)(({ theme }) => {
  const palette = theme.customColorPalette;
  const isDarkMode = theme.palette.mode === "dark";
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 8px",
    cursor: "pointer",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: isDarkMode ? palette.gray[800] : palette.blue[50],
    },
  };
});

const ContextMenuPaper = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  backgroundColor: isDarkMode ? palette.gray[800] : palette.gray[50],
  border: `1px solid ${isDarkMode ? palette.gray[700] : palette.gray[200]}`,
  borderRadius: "8px",
  minWidth: "160px",
  boxShadow: isDarkMode
    ? "0 4px 12px rgba(0,0,0,0.4)"
    : "0 4px 12px rgba(0,0,0,0.1)",
});

const renameMenuItemSx = (isDarkMode: boolean, palette: CustomColorPalette) => ({
  fontSize: "13px",
  fontFamily: "Roboto",
  color: isDarkMode ? palette.gray[200] : palette.gray[700],
  gap: "8px",
  "&:hover": {
    backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
  },
});

const deleteMenuItemSx = (isDarkMode: boolean) => ({
  fontSize: "13px",
  fontFamily: "Roboto",
  color: isDarkMode ? "#ef4444" : "#dc2626",
  gap: "8px",
  "&:hover": {
    backgroundColor: isDarkMode
      ? "rgba(239,68,68,0.1)"
      : "rgba(220,38,38,0.1)",
  },
});

interface FolderItemProps {
  folder: Folder;
  customItems?: React.ReactNode[];
  onFlowRename?: (flow: Flows) => void;
  onFlowDelete?: (flow: Flows) => void;
  onFlowShare?: (flow: Flows) => void;
  onFlowOpenInNewTab?: (flow: Flows) => void;
  onFlowCreateInterface?: (flow: Flows, folderId: string) => void;
  onFlowAddToLibrary?: (flow: Flows, folderId: string) => void;
  onFlowDuplicate?: (flow: Flows, folderId: string) => void;
  onFolderRename?: (folder: Folder) => void;
  onFolderDelete?: (folder: Folder) => void;
}

export const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  customItems,
  onFlowRename,
  onFlowDelete,
  onFlowShare,
  onFlowOpenInNewTab,
  onFlowCreateInterface,
  onFlowAddToLibrary,
  onFlowDuplicate,
  onFolderRename,
  onFolderDelete,
}) => {
  const { isDarkMode, palette } = useAgentsSidebarContext();
  const [isOpen, setIsOpen] = useState(false);
  const [localFolder, setLocalFolder] = useState(folder);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  useEffect(() => {
    setLocalFolder((prev) => ({
      ...prev,
      ...folder,
      folders: folder.folders?.length ? folder.folders : prev.folders,
      flows: folder.flows?.length ? folder.flows : prev.flows,
    }));
  }, [folder]);

  const textColor = isDarkMode ? palette.gray[300] : palette.gray[600];

  const hasItems = () => {
    return (
      (customItems?.length ?? 0) > 0 ||
      (localFolder.flows?.length ?? 0) > 0 ||
      (localFolder.folders?.length ?? 0) > 0
    );
  };

  const handleToggle = async () => {
    if (!isOpen && (!localFolder.flows || localFolder.flows.length === 0)) {
      if (!isVirtualFolderId(folder.id)) {
        try {
          const res = await FolderService.getOne(folder.id);
          setLocalFolder((prev) => ({
            ...prev,
            ...res.data,
            folders: prev.folders?.length ? prev.folders : res.data.folders,
          }));
        } catch (error) {
          console.error("Failed to load folder:", error);
        }
      }
    }
    setIsOpen(!isOpen);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (isVirtualFolderId(folder.id)) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  return (
    <Box>
      <FolderHeader
        onClick={handleToggle}
        onContextMenu={handleContextMenu}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <FolderIcon size={16} color={textColor} />
          <Typography
            sx={{
              color: textColor,
              fontFamily: "Roboto",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "20px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {folder.name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", color: textColor }}>
          {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </Box>
      </FolderHeader>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: ContextMenuPaper(isDarkMode, palette),
          },
        }}
      >
        {onFolderRename && (
          <MenuItem
            onClick={() => {
              handleCloseContextMenu();
              onFolderRename(folder);
            }}
            sx={renameMenuItemSx(isDarkMode, palette)}
          >
            <PencilSimpleLine size={16} />
            Rename
          </MenuItem>
        )}
        {onFolderDelete && (
          <MenuItem
            onClick={() => {
              handleCloseContextMenu();
              onFolderDelete(folder);
            }}
            sx={deleteMenuItemSx(isDarkMode)}
          >
            <Trash size={16} />
            Delete
          </MenuItem>
        )}
      </Menu>
      <Collapse in={isOpen}>
        <Box sx={{ marginLeft: "8px" }}>
          {hasItems() ? (
            customItems ? (
              customItems
            ) : (
              <>
                {localFolder.folders?.map((subfolder) => (
                  <FolderItem
                    key={subfolder.id}
                    folder={subfolder}
                    onFlowRename={onFlowRename}
                    onFlowDelete={onFlowDelete}
                    onFlowShare={onFlowShare}
                    onFlowOpenInNewTab={onFlowOpenInNewTab}
                    onFlowCreateInterface={onFlowCreateInterface}
                    onFlowAddToLibrary={onFlowAddToLibrary}
                    onFlowDuplicate={onFlowDuplicate}
                    onFolderRename={onFolderRename}
                    onFolderDelete={onFolderDelete}
                  />
                ))}
                {localFolder.flows?.map((flow) => (
                  <AgentItem
                    key={flow.id}
                    flow={flow}
                    folderId={folder.id}
                    onRename={onFlowRename}
                    onDelete={onFlowDelete}
                    onShare={onFlowShare}
                    onOpenInNewTab={onFlowOpenInNewTab}
                    onCreateInterface={onFlowCreateInterface}
                    onAddToLibrary={onFlowAddToLibrary}
                    onDuplicate={onFlowDuplicate}
                  />
                ))}
              </>
            )
          ) : (
            <Typography
              sx={{
                color: isDarkMode ? palette.gray[500] : palette.gray[400],
                fontFamily: "Roboto",
                fontSize: "12px",
                fontStyle: "italic",
                padding: "8px 16px",
              }}
            >
              No items
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
